import React from 'react';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from 'shared/components/ui/HoverTooltip';
import { Icon } from 'react-icons-kit';
import { ic_delete_outline } from 'react-icons-kit/md/ic_delete_outline';
import { inputClasses } from 'styles/tailwindClassnames';
import { AdditionalField, Transform } from './types';
import {
  valAt,
  applyTransformsJs,
  formatPreviewText,
  normalizeTransforms,
} from './helpers';
import { TransformAddButton, TransformPills } from './TransformControls';
import { MappingChip, EmptySlot } from './SlotParts';
import { SLOT_ROW } from './styles';
import { DragPathRef, StartPick, useDragOver } from './dragAndDrop';

function AdditionalRow({
  field,
  index,
  onUpdate,
  onRemove,
  sampleInput,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
  transformsLocked,
}: {
  field: AdditionalField;
  index: number;
  onUpdate: (index: number, field: AdditionalField) => void;
  onRemove: (index: number) => void;
  sampleInput: any;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick?: StartPick;
  sourceArmed?: boolean;
  transformsLocked?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const pathSegments = field.path ? field.path.split('.') : null;
  const previewValue = pathSegments
    ? valAt(sampleInput, pathSegments)
    : undefined;
  const pathNotInSample = field.path && previewValue === undefined;
  const transformedValue = applyTransformsJs(
    previewValue,
    field.transforms,
    sampleInput,
  );
  const setFieldTransforms = (next: Transform[]) =>
    onUpdate(index, {
      ...field,
      transforms: next.length ? normalizeTransforms(next) : undefined,
    });
  const { t } = useTranslation();

  const setPath = (p: string[]) =>
    onUpdate(index, { ...field, path: p.join('.') });

  const wand = !readOnly ? (
    <TransformAddButton
      onAdd={(tr) => setFieldTransforms([...(field.transforms ?? []), tr])}
      disabled={transformsLocked}
      allowedKinds={
        field.path || field.transforms?.some((tr) => tr.kind === 'coalesce')
          ? undefined
          : ['default', 'coalesce']
      }
    />
  ) : undefined;

  return (
    <div data-slot-row tabIndex={-1} className={SLOT_ROW}>
      {/* The trash beside the name removes the whole row; the chip's × below
          only clears the mapping, like a core slot. */}
      <div className="mb-1.5 flex items-center gap-2">
        <input
          value={field.name}
          onChange={(e) => onUpdate(index, { ...field, name: e.target.value })}
          placeholder={t('field name')}
          disabled={readOnly}
          aria-label={t('field name')}
          className={`${inputClasses.standard} min-w-0 flex-1 font-mono text-body-sm`}
        />
        {!readOnly && (
          <HoverTooltip
            content={t('Remove field')}
            position="top"
            focusable={false}
            portal
          >
            <button
              type="button"
              onClick={() => onRemove(index)}
              aria-label={t('Remove field')}
              className="flex shrink-0 items-center text-greyscale-5 hover:text-ui-status-error-destructive"
            >
              <Icon icon={ic_delete_outline} size={20} />
            </button>
          </HoverTooltip>
        )}
      </div>
      {pathSegments ? (
        <MappingChip
          path={pathSegments}
          previewValue={previewValue}
          notInSample={!!pathNotInSample}
          readOnly={readOnly}
          onClear={() => onUpdate(index, { ...field, path: '' })}
          clearLabel={t('Clear mapping')}
          wand={wand}
        />
      ) : (
        <EmptySlot
          label={t('Drop a source field')}
          isOver={isOver}
          sourceArmed={!!sourceArmed}
          readOnly={readOnly}
          handlers={handlers}
          onActivate={(el) => onStartPick?.(setPath, el)}
          onDropPath={() => {
            resetOver();
            if (dragPathRef.current) setPath(dragPathRef.current);
          }}
          wand={wand}
        />
      )}
      {field.transforms && field.transforms.length > 0 && (
        <TransformPills
          transforms={field.transforms}
          onChange={setFieldTransforms}
          readOnly={readOnly || transformsLocked}
          previewText={formatPreviewText(transformedValue)}
        />
      )}
    </div>
  );
}

export default AdditionalRow;
